import { StyleInfoRecord } from "@/lib/catalog"
import type { ProjectStyleTypes } from "./style-state"

type StyleLike = Pick<StyleInfoRecord, "author" | "globalBiases"> | null | undefined
type FinalizeStyleLike =
	| {
			name?: string | null
			presets?: Record<string, unknown> | null
	  }
	| null
	| undefined

type FinalizeStyleCreationMethod =
	| "Pixieset Gallery"
	| "Upload"
	| "Manual Edit Express Setup"
	| "Manual Edit Advanced Setup"
	| "Final Edits"
	| null

type FinalizeStyleStatus = "New" | "Existing"

const PRETRAINED_STYLE_AUTHORS = new Set(["pixieset", "polarr"])

const getIsPretrainedStyle = (style?: Pick<StyleInfoRecord, "author"> | null) => {
	const authorName = style?.author?.name?.toLowerCase().trim() ?? ""
	return PRETRAINED_STYLE_AUTHORS.has(authorName)
}

const getTuningValue = (value: unknown) => {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return 0
	}

	return Math.max(-5, Math.min(5, Number(value.toFixed(2))))
}

export const getApplyAIStyleMode = (style?: Pick<StyleInfoRecord, "author"> | null) => {
	return getIsPretrainedStyle(style) ? "pretrained_style" : "user_style"
}

export const getApplyAIStyleProperties = (style: StyleLike, isReapply = false) => {
	return {
		isReapply,
		styleMode: getApplyAIStyleMode(style),
		Exposure: getTuningValue(style?.globalBiases?.exposure),
		Contrast: getTuningValue(style?.globalBiases?.contrast),
		Saturation: getTuningValue(style?.globalBiases?.saturation),
		Temperature: getTuningValue(style?.globalBiases?.temperature),
		Tint: getTuningValue(style?.globalBiases?.tint),
	}
}

const getFinalizeStyleStatus = (projectStyleType: ProjectStyleTypes): FinalizeStyleStatus => {
	if (
		projectStyleType === "My AI Style" ||
		projectStyleType === "Pixieset AI Style" ||
		projectStyleType === "Polarr AI Style" ||
		projectStyleType === "Reapply"
	) {
		return "Existing"
	}

	return "New"
}

const getFinalizeStyleType = (projectStyleType: ProjectStyleTypes) => {
	if (projectStyleType === "Pixieset AI Style" || projectStyleType === "Polarr AI Style") {
		return "Pixieset AI Style"
	}

	return "My AI Style"
}

const getStyleCreationMethod = (
	projectStyleType: ProjectStyleTypes,
	isExpressSetup?: boolean,
	styleCreationMethod?: Exclude<FinalizeStyleCreationMethod, null>,
): FinalizeStyleCreationMethod => {
	if (styleCreationMethod) {
		return styleCreationMethod
	}

	switch (projectStyleType) {
		case "Pixieset Gallery":
			return "Pixieset Gallery"
		case "Images":
			return "Upload"
		case "Editing":
			return isExpressSetup ? "Manual Edit Express Setup" : "Manual Edit Advanced Setup"
		default:
			return null
	}
}

export const getFinalizeAIStyleProperties = (options: {
	projectStyleType: ProjectStyleTypes
	style: FinalizeStyleLike
	projectId?: string | null
	isExpressSetup?: boolean
	styleCreationMethod?: Exclude<FinalizeStyleCreationMethod, null>
	styleCreationIncludePreset?: boolean | null
	styleStatus?: FinalizeStyleStatus
}) => {
	const {
		projectStyleType,
		style,
		projectId,
		isExpressSetup,
		styleCreationMethod,
		styleCreationIncludePreset: styleCreationIncludePresetOverride,
		styleStatus: styleStatusOverride,
	} = options
	const styleStatus = styleStatusOverride ?? getFinalizeStyleStatus(projectStyleType)
	const creationMethod = getStyleCreationMethod(
		projectStyleType,
		isExpressSetup,
		styleCreationMethod,
	)
	const styleCreationIncludePreset =
		styleCreationIncludePresetOverride !== undefined
			? styleCreationIncludePresetOverride
			: styleStatus === "New"
				? Object.keys(style?.presets ?? {}).length > 0
				: null

	return {
		projectId: projectId ?? null,
		styleType: getFinalizeStyleType(projectStyleType),
		styleStatus,
		styleCreationMethod: creationMethod,
		styleCreationIncludePreset,
		styleName: style?.name ?? null,
	}
}

export const getExportStyleMode = (options: {
	style?: Pick<StyleInfoRecord, "author"> | null
	isManual?: boolean
}) => {
	if (options.isManual || !options.style) {
		return "manual edit"
	}

	return getApplyAIStyleMode(options.style)
}
